// Shapes that mirror the API contracts in
// Levitate-API/docs/api/meta-managed-ads.md.

export interface IMetaGlobalConnectionStatus {
	checkedAt: string;
	tokenConfigured: boolean;
	verificationSucceeded: boolean;
	systemUserId?: string;
	systemUserName?: string;
	error?: string;
}

interface ILevitateAccountRef {
	id: string;
	// The API may not have a display name for an account (e.g. internal/demo accounts).
	name: string | null;
}

export interface IMetaVisibleAsset {
	metaAdAccountId: string;
	name: string;
	accountStatus: number;
	businessName: string;
	isClientAccount: boolean;
	ownedByLevitateAccounts: ILevitateAccountRef[];
	managedByLevitateAccounts: ILevitateAccountRef[];
	unmapped: boolean;
}

// Records a sweep failure on the connection projection. `metaAdAccountId` is
// empty when the failure was at the connection level (e.g. token unavailable).
export interface IMetaMetricsSyncError {
	metaAdAccountId: string;
	timestamp: string;
	message: string;
}

// Shape returned by `GET admin/ads/meta/connections/{levitateAccountId}` and the
// assign/unassign mutations. The GET and the mutation responses now carry the
// same `ManagedConnectionResponse` shape on the backend — every field below
// (other than `levitateAccountId` and `managedAdAccountIds`) is optional only
// because a brand-new synthesized connection from the 404 fallback won't
// populate them.
export interface IMetaConnectionState {
	levitateAccountId: string;
	managedAdAccountIds: string[];
	// Customer's own OAuth-selected ad accounts (distinct from ads-team-managed).
	adAccountIds?: string[];
	hasCustomerToken: boolean;
	// Lead-ingestion health. The admin surface reports these regardless of
	// customer-OAuth state — a managed-only connection polls via the System User
	// token and its health must remain visible.
	lastPollTimestamp?: string;
	lastPollLeadCount?: number;
	lastPollError?: string;
	lastLeadReceivedAt?: string;
	lastTokenRefreshedAt?: string;
	// Metrics-sync health. Null `lastMetricsSyncAt` means the connection has
	// never been swept; a non-null `lastMetricsSyncError` is the most-recent
	// per-account failure (cleared on a fully-successful sweep).
	lastMetricsSyncAt?: string;
	lastMetricsSyncError?: IMetaMetricsSyncError;
}

export interface IAssignManagedRequest {
	levitateAccountId: string;
	metaAdAccountIds: string[];
}

export interface IUnassignManagedRequest {
	levitateAccountId: string;
	metaAdAccountId: string;
}

export const META_AD_ACCOUNT_STATUS_LABELS: Record<
	number,
	{ label: string; tone: 'success' | 'warning' | 'danger' | 'neutral' }
> = {
	1: { label: 'Active', tone: 'success' },
	2: { label: 'Disabled', tone: 'warning' },
	3: { label: 'Unsettled', tone: 'warning' },
	7: { label: 'Pending risk review', tone: 'warning' },
	8: { label: 'Pending settlement', tone: 'warning' },
	9: { label: 'In grace period', tone: 'warning' },
	100: { label: 'Pending closure', tone: 'danger' },
	101: { label: 'Closed', tone: 'danger' },
	201: { label: 'Any active', tone: 'success' },
	202: { label: 'Any closed', tone: 'danger' },
};
